import React from "react";
import styled from "styled-components";

import brk from "../../util/breakpoints";
import Container from "../Container";
import Reveal from "../Reveal";
import SectionHeading, { Marker, Signal } from "../SectionHeading";

import HeadshotImg from "../../img/headshot.jpg";
import BeeTogetherImg from "../../img/beeTogether.png";

const chapters = [
  {
    label: "Web",
    description: "React, TypeScript and Node across fintech, logistics and collaboration products.",
  },
  {
    label: "Mobile",
    description: "React Native apps, including one used by more than a million people.",
  },
  {
    label: "Games",
    description: "Unity and C# projects, from multiplayer prototypes to a game jam winner.",
  },
];

const building = {
  project: "Bee Together",
  pitch:
    "A cozy first-person co-op game: build a buzzing little bee farm with friends, care for adorable bees, and turn their bounty into a whimsical honey haven.",
  traits: ["First-person", "Co-op", "Cozy farming"],
  img: BeeTogetherImg,
};

const Wrapper = styled.section`
  width: 100%;
  padding: 104px 0;
  background: var(--surface);
  @media ${brk.device.tablet} {
    padding: 72px 0;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: stretch;
  gap: 74px;
  margin-top: 42px;
  @media ${brk.device.pc} {
    flex-direction: column;
    gap: 42px;
  }
`;

const Portrait = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
  width: 520px;
  min-height: 540px;
  padding: 28px;
  border: 1px solid var(--borderStrong);
  border-radius: 22px;
  background: var(--card);
  overflow: hidden;
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(rgba(8, 10, 16, 0) 55%, rgba(8, 10, 16, 0.88));
  }
  @media ${brk.device.pc} {
    width: 100%;
    max-width: 520px;
    min-height: 0;
    aspect-ratio: 26 / 27;
  }
`;

const PortraitCaption = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 1;
  span {
    color: var(--lime);
    font-family: var(--fontMono);
    font-size: 9px;
    text-transform: uppercase;
  }
  p {
    color: var(--textMuted);
    font-family: var(--fontBody);
    font-size: 12px;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 28px;
  flex: 1;
  min-width: 0;
`;

const Narrative = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Statement = styled.p`
  color: var(--textMuted);
  font-family: var(--fontBody);
  font-size: 18px;
  line-height: 1.55;
  @media ${brk.device.tablet} {
    font-size: 16px;
  }
`;

const Chapter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;
  padding: 17px 0;
  border-top: 1px solid var(--border);
`;

const ChapterMarker = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border: 1px solid var(--borderStrong);
  border-radius: 50%;
  color: var(--lime);
  font-family: var(--fontMono);
  font-size: 9px;
`;

const ChapterLabel = styled.p`
  width: 150px;
  color: var(--text);
  font-family: var(--fontHeading);
  font-size: 16px;
`;

const ChapterDescription = styled.p`
  flex: 1;
  min-width: 0;
  color: var(--textMuted);
  font-family: var(--fontBody);
  font-size: 13px;
  line-height: 1.45;
  @media ${brk.device.tablet} {
    flex: none;
    width: 100%;
    padding-left: 52px;
    margin-top: -8px;
  }
`;

const Building = styled.div`
  display: flex;
  align-items: stretch;
  gap: 24px;
  padding: 28px;
  border: 1px solid var(--border);
  border-radius: 22px;
  background: var(--card);
  @media ${brk.device.tablet} {
    display: none;
  }
`;

const BuildCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
  min-width: 0;
  h3 {
    margin-top: 14px;
    color: var(--text);
    font-family: var(--fontHeading);
    font-size: 28px;
    font-weight: 700;
  }
`;

const BuildFocus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  h4 {
    color: var(--lime);
    font-family: var(--fontHeading);
    font-size: 25px;
    font-weight: 700;
  }
  p {
    color: var(--textMuted);
    font-family: var(--fontBody);
    font-size: 13px;
    line-height: 1.5;
  }
`;

const BuildTraits = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
  span {
    padding: 6px 10px;
    border: 1px solid var(--border);
    border-radius: 999px;
    color: var(--textMuted);
    font-family: var(--fontMono);
    font-size: 9px;
    text-transform: uppercase;
    white-space: nowrap;
  }
`;

const BuildImage = styled.div`
  flex-shrink: 0;
  width: 46%;
  min-height: 200px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background-image: url(${(props) => props.$img});
  background-position: center;
  background-size: cover;
`;

function About() {
  return (
    <Wrapper id="about">
      <Container>
        <Reveal>
          <SectionHeading label="01 / About me" title="James Spears" />
        </Reveal>
        <Reveal delay={100}>
          <Content>
            <Portrait>
              <img alt="James Spears" src={HeadshotImg} />
              <PortraitCaption>
                <span>Software engineer</span>
                <p>Currently at Atlassian.</p>
              </PortraitCaption>
            </Portrait>
            <Copy>
              <Narrative>
                <Statement>
                  I'm a software engineer who has shipped web, mobile and game
                  products for early-stage startups and established companies
                  alike, most recently at Atlassian. My work has spanned
                  fintech, logistics, AI and collaboration software. I care
                  about building things that are reliable underneath and
                  enjoyable to use, which is also why I make games.
                </Statement>
                <div>
                  {chapters.map((chapter, index) => {
                    return (
                      <Chapter key={chapter.label}>
                        <ChapterMarker>{`0${index + 1}`}</ChapterMarker>
                        <ChapterLabel>{chapter.label}</ChapterLabel>
                        <ChapterDescription>
                          {chapter.description}
                        </ChapterDescription>
                      </Chapter>
                    );
                  })}
                </div>
              </Narrative>
              <Building>
                <BuildCopy>
                  <div>
                    <Marker>
                      <Signal />
                      Now
                    </Marker>
                    <h3>Currently building</h3>
                  </div>
                  <BuildFocus>
                    <h4>{building.project}</h4>
                    <p>{building.pitch}</p>
                  </BuildFocus>
                  <BuildTraits>
                    {building.traits.map((trait) => {
                      return <span key={trait}>{trait}</span>;
                    })}
                  </BuildTraits>
                </BuildCopy>
                <BuildImage
                  $img={building.img}
                  role="img"
                  aria-label={`${building.project} screenshot`}
                />
              </Building>
            </Copy>
          </Content>
        </Reveal>
      </Container>
    </Wrapper>
  );
}

export default About;
